// Canvas-based image downscaler.
//
// Runs in the browser before upload so we don't push 8MB phone photos
// across the wire when a 1600px JPEG would do. The backend re-encodes
// + strips EXIF anyway (sharp), so this is purely a payload-size
// optimization, not a trust boundary.

const DEFAULT_MAX_EDGE = 1600
const DEFAULT_JPEG_QUALITY = 0.82

/**
 * Downscale + re-encode a File to JPEG, returning a new File.
 *
 * Returns the original File untouched when:
 *   - it isn't a recognisable image (e.g. .heic the browser can't decode)
 *   - it's already smaller than the max edge AND under 500KB
 *   - any step in the canvas pipeline throws
 *
 * @param {File} file
 * @param {Object} [opts]
 * @param {number} [opts.maxEdge=1600] longest edge in pixels
 * @param {number} [opts.quality=0.82] JPEG quality 0..1
 */
export async function compressImage(file, opts = {}) {
    const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE
    const quality = opts.quality != null ? opts.quality : DEFAULT_JPEG_QUALITY

    if (!file || !file.type || !file.type.startsWith('image/')) return file

    try {
        const bitmap = await loadBitmap(file)
        const { width, height } = scaledDimensions(
            bitmap.width,
            bitmap.height,
            maxEdge
        )

        // Skip work when the image is already small AND lightweight —
        // re-encoding tiny files would actually make them bigger via
        // JPEG overhead.
        if (
            width === bitmap.width &&
            height === bitmap.height &&
            file.size < 500 * 1024
        ) {
            return file
        }

        const canvas = document.createElement('canvas')
        canvas.width = width
        canvas.height = height
        const ctx = canvas.getContext('2d')
        ctx.drawImage(bitmap, 0, 0, width, height)
        // ImageBitmap holds GPU resources — close it explicitly so
        // the browser can reclaim memory before the next file.
        if (bitmap.close) bitmap.close()

        const blob = await new Promise((resolve) =>
            canvas.toBlob(resolve, 'image/jpeg', quality)
        )
        if (!blob) return file

        // Replace the extension with .jpg so the backend's mime-type
        // checks and the S3 key match the actual bytes.
        const newName = file.name.replace(/\.[^.]+$/, '') + '.jpg'
        return new File([blob], newName, {
            type: 'image/jpeg',
            lastModified: file.lastModified || Date.now(),
        })
    } catch (e) {
        // Any failure (HEIC the browser can't decode, OOM on huge
        // files, etc.) falls back to the original — the upload will
        // still work, just heavier.
        console.warn('[compressImage] falling back to original:', e)
        return file
    }
}

// createImageBitmap is widely supported and faster than the <img>
// onload dance; fall back to <img> when it's missing (old Safari).
function loadBitmap(file) {
    if (typeof createImageBitmap === 'function') {
        return createImageBitmap(file)
    }
    return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file)
        const img = new Image()
        img.onload = () => {
            URL.revokeObjectURL(url)
            resolve(img)
        }
        img.onerror = (e) => {
            URL.revokeObjectURL(url)
            reject(e)
        }
        img.src = url
    })
}

// Scale (width, height) so the longest edge is at most `maxEdge`,
// keeping the aspect ratio. Returns original dimensions if both
// edges are already within budget.
function scaledDimensions(w, h, maxEdge) {
    const longest = Math.max(w, h)
    if (longest <= maxEdge) return { width: w, height: h }
    const scale = maxEdge / longest
    return {
        width: Math.round(w * scale),
        height: Math.round(h * scale),
    }
}
